import { FormControl, FormLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SegmentedSwitch } from '../../../../components/segmented-switch';

type DocumentUploader = 'CONSULTANT' | 'CLIENT';

interface ConsultantDocumentUploaderFieldProps {
  /** `true` = il documento (e i rinnovi) lo carica il cliente; `false` = lo carica il consulente. */
  clientCanUpload: boolean;
  onChange: (clientCanUpload: boolean) => void;
  /** Alla creazione il consulente allega il documento subito, per un solo cliente: i testi lo dicono. */
  isCreation?: boolean;
  testIdPrefix: string;
}

function useUploaderHelpers(isCreation: boolean): Record<DocumentUploader, string> {
  const { t } = useTranslation();
  return {
    CONSULTANT: isCreation
      ? t('consultant.deadlines.uploader.consultantCreateHelper', {
          defaultValue:
            'Alleghi il documento adesso, per un solo cliente. Anche i rinnovi li carichi tu.',
        })
      : t('consultant.deadlines.uploader.consultantHelper', {
          defaultValue: 'Carichi tu il documento e i rinnovi.',
        }),
    CLIENT: isCreation
      ? t('consultant.deadlines.uploader.clientCreateHelper', {
          defaultValue:
            'Il cliente lo carica dalla sua sezione Documenti, anche i rinnovi. Puoi scegliere più clienti.',
        })
      : t('consultant.deadlines.uploader.clientHelper', {
          defaultValue: 'Il cliente lo carica dalla sua sezione Documenti, anche i rinnovi.',
        }),
  };
}

/** "Chi carica il documento?": stesso campo `clientCanUpload` della scadenza, vale anche per i rinnovi. */
export function ConsultantDocumentUploaderField({
  clientCanUpload,
  onChange,
  isCreation = false,
  testIdPrefix,
}: ConsultantDocumentUploaderFieldProps) {
  const { t } = useTranslation();
  const helpers = useUploaderHelpers(isCreation);
  const legendId = `${testIdPrefix}-legend`;
  const helperId = `${testIdPrefix}-helper`;
  const selected: DocumentUploader = clientCanUpload ? 'CLIENT' : 'CONSULTANT';

  return (
    <FormControl sx={{ alignItems: 'flex-start', gap: 1 }}>
      <FormLabel id={legendId}>
        {t('consultant.deadlines.uploader.title', { defaultValue: 'Chi carica il documento?' })}
      </FormLabel>
      <SegmentedSwitch<DocumentUploader>
        value={selected}
        onChange={(next) => onChange(next === 'CLIENT')}
        ariaLabelledBy={legendId}
        ariaDescribedBy={helperId}
        testId={testIdPrefix}
        options={[
          {
            value: 'CONSULTANT',
            label: t('consultant.deadlines.uploader.consultant', { defaultValue: 'Lo carico io' }),
            testId: `${testIdPrefix}-CONSULTANT`,
          },
          {
            value: 'CLIENT',
            label: t('consultant.deadlines.uploader.client', {
              defaultValue: 'Lo carica il cliente',
            }),
            testId: `${testIdPrefix}-CLIENT`,
          },
        ]}
      />
      <Typography id={helperId} variant="caption" color="text.secondary" aria-live="polite">
        {helpers[selected]}
      </Typography>
    </FormControl>
  );
}
